// Working together has an end (decided 11-05, feedback 11-04: a 5-minute card ran for 4 h 44 min and nobody asked).
// At the planned minutes she asks once: Done / Keep going / Stuck. After Keep going (or Stuck) she asks once more after
// another planned length, then never again in that session. No answer for 30 minutes: the session ends by itself and
// the card is back on today's list. Her lines here; the server writes them (kind 'work'), the app shows them (phone
// chat and Working together, the room's subtitles, the small window) and the notification uses the same words.
// Also: her "Start together" offer under her words (kind 'start', the offer_start tool).
// Tokens: {Task} the card's short name (before ':'), {mins} 'five minutes', {n} buddy name, {title} the full title.
import type { CharId } from '../types';
import { taskShort } from '../rules';
import { C } from './characters';
import { fill } from './lines';

export interface WorkLines {
  ask: string;    // n 1, at the planned minutes
  ask2: string;   // n 2, after Keep going (or Stuck) and another planned length
  keep: string;   // after Keep going on ask 1: she'll ask once more
  keep2: string;  // after Keep going on ask 2: no more asks this session
}

export const WORK: Record<CharId, WorkLines> = {
  sunny: {
    ask: 'Time check: {mins} on {Task}. Done, or one more set?',
    ask2: 'Checking on {Task}. Done, or is it doing a really long bit?',
    keep: "Fine. I'll pretend I'm not watching. Back in {mins}.",
    keep2: "Okay, no more check-ins. I'll sit here quietly. Mostly.",
  },
  margo: {
    ask: "That's your {mins} on {Task}. Done, or keep going?",
    ask2: 'Checking in on {Task}. Done, or still running?',
    keep: "Okay. Holding. I'll check back in {mins}.",
    keep2: "Okay. No more cues from me. Tap Done when it's done.",
  },
  arjun: {
    ask: "That's {mins} with {Task}. Is it done, or would you like to keep going?",
    ask2: 'How is {Task} coming along? Done, or a little more?',
    keep: "Take your time. I'll look up again in {mins}.",
    keep2: "Alright. I won't ask again. I'm right here.",
  },
  tavo: {
    ask: "That's {mins} on {Task}! In the books, or extra innings?",
    ask2: 'Still up to bat with {Task}! Done, or a few more pitches?',
    keep: "Extra innings it is! I'll check back in {mins}.",
    keep2: 'Alright, no more calls from the booth! Finish strong.',
  },
};

/** Buttons under her ask (phone chat, Working together, the room, the small window) and the user's line for Keep going. */
export const WORK_BTN = { done: 'Done', keep: 'Keep going', stuck: 'Stuck' };
export const WORK_SAID = { keep: 'Keep going.' };
/** Note lines (not her voice) once an ask is settled without Done: the session ended by itself after no answer. */
export const WORK_NOTE = { ended: '“{Task}” is back on today’s list' };
/** Her offer's button and its note once taken (kind 'start'). */
export const START_BTN = 'Start together';
/** Her words with the offer when she has none of her own (fake mode, the app mock). */
export const START_LINE: Record<CharId, string> = {
  sunny: "Love the energy. Tap Start together and I'll pretend to work too.",
  margo: "Good. Start together whenever you're ready.",
  arjun: "Alright. Start together whenever you're ready. I'll be here.",
  tavo: "Let's play ball! Tap Start together.",
};
export const START_NOTE = { started: 'Working on it with {n}: {Task}' };
/** The room's question before switching cards while working together on another one. */
export const SWITCH = {
  ask: { sunny: 'Wait. Did {Task} get done first?', margo: 'Before we switch: is {Task} done?', arjun: 'Before we move on: is {Task} done?', tavo: 'Hold up! Is {Task} in the books first?' } as Record<CharId, string>,
  yes: 'Yes, it’s done', no: 'Not yet',
};

/** {Task} in her lines: the card's short name, without a step's prefix ("Two minutes: Q3 report" -> "Q3 report"; the
 *  two-minute offer and the stuck helps make such titles, and "your two minutes on Two minutes" read badly). */
export const workTask = (title: string) => taskShort(String(title || '').replace(/^\s*(two minutes|rough version)\s*:\s*/i, '')) || taskShort(title);

const WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
/** 5 -> 'five minutes', 1 -> 'one minute', 25 -> '25 minutes'. */
export const minutesWord = (m: number) => {
  const k = Math.max(1, Math.round(m));
  return `${k <= 10 ? WORDS[k] : k} ${k === 1 ? 'minute' : 'minutes'}`;
};
/** Wait before ask 2 and between Keep going and the next ask: the planned length, at least 5 minutes. */
export const workAgainMin = (min: number) => Math.max(5, Math.round(min) || 5);
/** Her ask (n 1 or 2) for what is being worked on. */
export const workAskText = (ch: CharId, n: 1 | 2, title: string, min: number) =>
  fill(n === 1 ? WORK[ch].ask : WORK[ch].ask2, { Task: workTask(title), mins: minutesWord(min), title });
/** Her line after Keep going on ask n. */
export const workKeepText = (ch: CharId, n: 1 | 2, min: number) => fill(n === 1 ? WORK[ch].keep : WORK[ch].keep2, { mins: minutesWord(workAgainMin(min)) });
export const workEndedNote = (title: string) => fill(WORK_NOTE.ended, { Task: workTask(title) });
export const startedNote = (ch: CharId, title: string) => fill(START_NOTE.started, { n: C[ch].name, Task: workTask(title) });
export const switchAsk = (ch: CharId, title: string) => fill(SWITCH.ask[ch], { Task: workTask(title) });
